/**
 * Screen 1: new or returning, who the visit is for, and the visit type.
 * Existing-patient types appear only once the visitor is identified (OB-07).
 */
import type { BookingFor, PublicSite, PublicType } from '@velora/contracts/booking';
import { Button } from '@velora/ui';
import {
  ChevronRight,
  HeartPulse,
  Smile,
  Sparkles,
  Stethoscope,
  UserRound,
  UsersRound,
} from 'lucide-react';
import type { ReactNode } from 'react';
import type { FamilyAnchor } from '../flow.js';
import { fitsAgeBand, lengthLabel, whenLabel } from '../lib/format.js';
import { radioKeyDown, radioTabIndex } from '../lib/radio.js';

interface Props {
  site: PublicSite;
  /** Visit types offered to someone booking for a family member (new or returning). */
  otherTypes: PublicType[] | null;
  patientKind: 'new' | 'existing';
  bookingFor: BookingFor;
  family: FamilyAnchor | null;
  identifyPanel: ReactNode;
  showIdentify: boolean;
  onPatientKind: (k: 'new' | 'existing') => void;
  onBookingFor: (v: BookingFor) => void;
  onChooseType: (typeId: string) => void;
  onSignOut: () => void;
}

function iconFor(t: PublicType) {
  if (t.urgent) return <HeartPulse size={20} aria-hidden />;
  const l = t.label.toLowerCase();
  if (/clean|hygiene|recall|check/.test(l)) return <Sparkles size={20} aria-hidden />;
  if (/child|kid/.test(l)) return <Smile size={20} aria-hidden />;
  return <Stethoscope size={20} aria-hidden />;
}

function Choice<T extends string>(props: {
  name: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string; icon: ReactNode; hint?: string }>;
  onChange: (v: T) => void;
}) {
  return (
    <div className="bw-choice" role="radiogroup" aria-label={props.name}>
      {props.options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={props.value === o.value}
          tabIndex={radioTabIndex(
            i,
            props.options.findIndex((x) => x.value === props.value),
          )}
          className="bw-choice__item"
          onClick={() => props.onChange(o.value)}
          onKeyDown={radioKeyDown}
        >
          <span className="bw-choice__icon">{o.icon}</span>
          <span className="bw-choice__text">
            <span className="bw-choice__label">{o.label}</span>
            {o.hint ? <span className="bw-choice__hint">{o.hint}</span> : null}
          </span>
        </button>
      ))}
    </div>
  );
}

export function VisitStep(p: Props) {
  const { site } = p;
  const identified = site.identity !== null;
  const forOther = p.bookingFor !== 'self';
  // An identified patient booking for someone else books that person with
  // typed details, so they see the visits offered to anyone (`otherTypes`).
  const types =
    identified && forOther
      ? (p.otherTypes ?? []).filter((t) => t.audience !== 'existing')
      : site.types.filter(
          (t) =>
            (identified || p.patientKind === 'existing'
              ? t.audience !== 'new'
              : t.audience !== 'existing') &&
            // A known patient only sees visits for their age (child or adult).
            fitsAgeBand(
              t.ageBand,
              identified ? (site.identity?.birthdate ?? null) : null,
              site.today,
            ),
        );
  const urgent = types.filter((t) => t.urgent);
  const regular = types.filter((t) => !t.urgent);

  return (
    <div className="bw-step" data-testid="step-visit">
      {p.family ? (
        <div className="bw-banner" data-tone="success">
          <UsersRound size={18} aria-hidden />
          <p>
            <strong>{p.family.patientFirstName}</strong> is set for{' '}
            {whenLabel(p.family.start, site.timezone, site.today)}. Next, choose a visit for the
            next family member. We will suggest times right before or after.
          </p>
        </div>
      ) : null}

      {identified && site.identity ? (
        <div className="bw-welcome" data-testid="welcome">
          <span className="bw-welcome__avatar" aria-hidden>
            {(site.identity.firstName[0] ?? '').toUpperCase()}
          </span>
          <div>
            <p className="bw-welcome__title">Welcome back, {site.identity.firstName}.</p>
            <p className="bw-dim">
              {site.identity.mobileMasked
                ? `We will text ${site.identity.mobileMasked} about this visit.`
                : 'Your details are filled in for you.'}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={p.onSignOut}>
            Not you?
          </Button>
        </div>
      ) : null}

      <section className="bw-section" aria-labelledby="bw-q-for">
        <h2 id="bw-q-for" className="bw-q">
          Who is the visit for?
        </h2>
        <Choice
          name="Who is the visit for"
          value={p.bookingFor}
          onChange={p.onBookingFor}
          options={[
            { value: 'self', label: 'Me', icon: <UserRound size={18} aria-hidden /> },
            {
              value: 'dependent',
              label: 'My child or family',
              icon: <UsersRound size={18} aria-hidden />,
            },
            {
              value: 'other',
              label: 'Someone else',
              icon: <UserRound size={18} aria-hidden />,
              hint: 'I care for them',
            },
          ]}
        />
      </section>

      {identified ? null : (
        <section className="bw-section" aria-labelledby="bw-q-new">
          <h2 id="bw-q-new" className="bw-q">
            {p.bookingFor === 'self'
              ? 'Have you been to our office before?'
              : 'Has the patient been to our office before?'}
          </h2>
          <Choice
            name="New or returning patient"
            value={p.patientKind}
            onChange={p.onPatientKind}
            options={[
              {
                value: 'new',
                label: p.bookingFor === 'self' ? 'I am new here' : 'New to the office',
                icon: <Sparkles size={18} aria-hidden />,
              },
              {
                value: 'existing',
                label: p.bookingFor === 'self' ? 'I have been before' : 'Has been before',
                icon: <UserRound size={18} aria-hidden />,
              },
            ]}
          />
          {p.showIdentify && p.bookingFor === 'self' ? p.identifyPanel : null}
        </section>
      )}

      <section className="bw-section" aria-labelledby="bw-q-type">
        <h2 id="bw-q-type" className="bw-q">
          What kind of visit?
        </h2>
        {types.length === 0 ? (
          <p className="bw-empty" data-testid="no-types">
            {p.patientKind === 'existing' && !identified
              ? 'Find your record above to see visits for returning patients.'
              : 'No visits can be booked online right now. Please call the office.'}
          </p>
        ) : (
          <ul className="bw-types" data-testid="type-list">
            {[...urgent, ...regular].map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className="bw-type"
                  data-urgent={t.urgent ? '' : undefined}
                  onClick={() => p.onChooseType(t.id)}
                >
                  <span className="bw-type__icon">{iconFor(t)}</span>
                  <span className="bw-type__body">
                    <span className="bw-type__label">{t.label}</span>
                    {t.description ? <span className="bw-type__desc">{t.description}</span> : null}
                    <span className="bw-type__meta">
                      {t.urgent ? 'Today or tomorrow' : lengthLabel(t.lengthMin)}
                      {t.ageBand === 'child'
                        ? ' · Children'
                        : t.ageBand === 'adult'
                          ? ' · Adults'
                          : ''}
                    </span>
                  </span>
                  <ChevronRight size={18} className="bw-type__chev" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
